import React, { useState } from 'react';
import { Type, Image, Shapes } from 'lucide-react';
import { useLanguage } from '../i18n';

interface ToolbarProps {
  activeTool: 'text' | 'image' | 'shape' | null;
  setActiveTool: (tool: 'text' | 'image' | 'shape' | null) => void;
  onShapeSelect?: (shapeType: 'rectangle' | 'circle' | 'triangle') => void;
}

const Toolbar: React.FC<ToolbarProps> = ({ activeTool, setActiveTool, onShapeSelect }) => {
  const { t } = useLanguage();
  const [showShapeMenu, setShowShapeMenu] = useState(false);
  
  return (
    <div style={{
      position: 'fixed',
      bottom: 24,
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#1a1a1a',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: 8,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1)',
      padding: '10px 14px',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      zIndex: 100,
      backdropFilter: 'blur(10px)',
    }}>
      <button
        style={{
          background: activeTool === 'text' ? '#1e48e0' : '#2a2a2a',
          border: activeTool === 'text' ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 6,
          width: 44,
          height: 44,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
          boxShadow: activeTool === 'text' ? '0 4px 12px rgba(30, 72, 224, 0.4)' : 'none',
        }}
        title={t('components.toolbar.addText')}
        onClick={() => setActiveTool(activeTool === 'text' ? null : 'text')}
        onMouseEnter={e => {
          if (activeTool !== 'text') {
            e.currentTarget.style.background = '#333';
            e.currentTarget.style.borderColor = '#1e48e0';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(30, 72, 224, 0.3)';
          }
        }}
        onMouseLeave={e => {
          if (activeTool !== 'text') {
            e.currentTarget.style.background = '#2a2a2a';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
      >
        <Type size={22} color={activeTool === 'text' ? '#fff' : '#d4d4d8'} strokeWidth={2} />
      </button>
      <button
        style={{
          background: activeTool === 'image' ? '#1e48e0' : '#2a2a2a',
          border: activeTool === 'image' ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 6,
          width: 44,
          height: 44,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
          boxShadow: activeTool === 'image' ? '0 4px 12px rgba(30, 72, 224, 0.4)' : 'none',
        }}
        title={t('components.toolbar.addImage')}
        onClick={() => setActiveTool(activeTool === 'image' ? null : 'image')}
        onMouseEnter={e => {
          if (activeTool !== 'image') {
            e.currentTarget.style.background = '#333';
            e.currentTarget.style.borderColor = '#1e48e0';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(30, 72, 224, 0.3)';
          }
        }}
        onMouseLeave={e => {
          if (activeTool !== 'image') {
            e.currentTarget.style.background = '#2a2a2a';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
      >
        <Image size={22} color={activeTool === 'image' ? '#fff' : '#d4d4d8'} strokeWidth={2} />
      </button>
      <div style={{ position: 'relative' }}
        onMouseEnter={() => setShowShapeMenu(true)}
        onMouseLeave={() => setShowShapeMenu(false)}
      >
        <button
          style={{
            background: activeTool === 'shape' ? '#1e48e0' : '#2a2a2a',
            border: activeTool === 'shape' ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 6,
            width: 44,
            height: 44,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
            boxShadow: activeTool === 'shape' ? '0 4px 12px rgba(30, 72, 224, 0.4)' : 'none',
          }}
          title={t('components.toolbar.addShape')}
          onClick={() => setActiveTool(activeTool === 'shape' ? null : 'shape')}
          onMouseEnter={e => {
            if (activeTool !== 'shape') {
              e.currentTarget.style.background = '#333';
              e.currentTarget.style.borderColor = '#1e48e0';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(30, 72, 224, 0.3)';
            }
          }}
          onMouseLeave={e => {
            if (activeTool !== 'shape') {
              e.currentTarget.style.background = '#2a2a2a';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.boxShadow = 'none';
            }
          }}
        >
          <Shapes size={22} color={activeTool === 'shape' ? '#fff' : '#d4d4d8'} strokeWidth={2} />
        </button>
        {showShapeMenu && (
          <div style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: 8,
            background: '#1a1a1a',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 8,
            padding: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
            zIndex: 101,
            minWidth: 120,
          }}>
            <button
              onClick={() => {
                onShapeSelect?.('rectangle');
                setShowShapeMenu(false);
              }}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 4,
                padding: '8px 12px',
                color: '#d4d4d8',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#2a2a2a';
                e.currentTarget.style.borderColor = '#1e48e0';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              <div style={{ width: 16, height: 16, background: '#1e48e0', borderRadius: 2 }}></div>
              <span>{t('components.toolbar.rectangle')}</span>
            </button>
            <button
              onClick={() => {
                onShapeSelect?.('circle');
                setShowShapeMenu(false);
              }}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 4,
                padding: '8px 12px',
                color: '#d4d4d8',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#2a2a2a';
                e.currentTarget.style.borderColor = '#1e48e0';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              <div style={{ width: 16, height: 16, background: '#1e48e0', borderRadius: '50%' }}></div>
              <span>{t('components.toolbar.circle')}</span>
            </button>
            <button
              onClick={() => {
                onShapeSelect?.('triangle');
                setShowShapeMenu(false);
              }}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 4,
                padding: '8px 12px',
                color: '#d4d4d8',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#2a2a2a';
                e.currentTarget.style.borderColor = '#1e48e0';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              <div style={{ width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: '16px solid #1e48e0' }}></div>
              <span>{t('components.toolbar.triangle')}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Toolbar;
